/**
 * CDB-TQS API — Transcript Refinement and Quality Analysis
 */

import { CDB_TQS_API_BASE_URL, getAuthHeaders } from "./http";

/** Why the model stopped generating, normalized by cdb_tqs (cdb_tqs#1377). */
export type RefineStopReason =
  | 'completed'
  | 'max_output_tokens'
  | 'content_filter'
  | 'failed'
  | 'unknown'

/** How far a chunked refinement has got (cdb_webos#907). */
export interface RefineProgress {
  done: number
  total: number
}

export interface RefineOptions {
  /**
   * The action has to return the whole text — clean up, formal, casual,
   * expand — so the backend may split it into parts rather than let the output
   * budget cut it off.
   *
   * The caller has to say, because the endpoint takes free-text instructions
   * and cannot tell: chunking a summarisation would return a string of partial
   * summaries instead of a summary.
   */
  lengthPreserving?: boolean
  /** Called per finished part while a split refinement runs. */
  onProgress?: (progress: RefineProgress) => void
}

export interface RefineTranscriptResponse {
  content: string
  /**
   * `max_output_tokens` means the text is CUT OFF at the end — which reading it
   * does not reveal, because a truncated refinement is well-formed at the top
   * and simply stops. Worth checking before the result is applied: the
   * transcript assistant replaces the transcript with it and saves a version.
   *
   * Optional because a backend that predates cdb_tqs#1377 omits it, and because
   * the JSON fallback path above can come from such a backend. Absent is not
   * `completed` — it means nobody said.
   */
  stop_reason?: RefineStopReason
  /**
   * How many parts the text was split into (cdb_webos#907); absent when it was
   * not split. A stitched answer was produced differently from a single-call
   * one, and that is worth knowing when chasing a seam.
   */
  chunks?: number | null
}

/**
 * Refine/transform transcript text using AI (structure-text endpoint).
 *
 * Asks for Server-Sent Events (cdb_tqs#1378). The refinement is a single LLM
 * round trip over the whole transcript, which sends nothing until it finishes —
 * and the gateway in front gives up first (30 s per `/api/*` request on the
 * staging Application Gateway). Keepalive comments keep the response alive
 * while the model works.
 *
 * A JSON answer is still handled, so this works against a backend that predates
 * the streaming shape — the two repos deploy independently.
 */
export async function refineTranscript(
  text: string,
  instructions: string,
  maxTokens = 4000,
  temperature = 0.3,
  systemPrompt?: string,
  options: RefineOptions = {}
): Promise<RefineTranscriptResponse> {
  const payload: Record<string, unknown> = {
    text,
    instructions,
    max_tokens: maxTokens,
    temperature,
  };
  if (systemPrompt) {
    payload.system_prompt = systemPrompt;
  }
  if (options.lengthPreserving) {
    payload.length_preserving = true;
  }
  const url = `${CDB_TQS_API_BASE_URL}/agent/structure-text`;
  const body = JSON.stringify(payload);

  const doFetch = (headers: Record<string, string>) =>
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...headers,
      },
      credentials: "include",
      body,
    });

  let response = await doFetch(await getAuthHeaders());
  // Retry once with a refreshed token, mirroring fetchWithLogging.
  if (response.status === 401) {
    const { authService } = await import("@/services/auth-service");
    const newToken = await authService.refreshAccessToken();
    if (newToken) {
      response = await doFetch({ Authorization: `Bearer ${newToken}` });
    }
  }

  if (!response.ok) {
    throw new Error(`structure-text failed: HTTP ${response.status}`);
  }

  // A backend that does not know the streaming shape answers as JSON even
  // though we asked for SSE. Decided on what came back, not on what we asked
  // for, so neither side has to know what the other is running.
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("text/event-stream")) {
    return (await response.json()) as RefineTranscriptResponse;
  }
  if (!response.body) {
    throw new Error("structure-text announced a stream without a body");
  }

  return readResultEvent<RefineTranscriptResponse>(
    response.body,
    "structure-text",
    options.onProgress
  );
}

/**
 * Read whatever the backend put in an `event: error` frame (cdb_tqs#898).
 *
 * Two shapes are in play. cdb_tqs#900 moved this endpoint onto the same
 * `StreamErrorEvent` envelope every other error frame uses — `{ error, code }` —
 * while the old hand-built `{ detail }` is what a backend before that release
 * sends. Both are handled here on purpose, so frontend and backend can be
 * deployed in either order.
 *
 * `code` is preferred because it is the stable half; `error` and `detail` are
 * English diagnostics. Nothing here reaches a user — the caller logs it — so
 * this resolves to the most useful DIAGNOSTIC string, not to a translation.
 */
export function describeErrorFrame(data: string): string {
  if (!data) return 'unknown error'
  let parsed: unknown
  try {
    parsed = JSON.parse(data)
  } catch {
    // Not JSON at all: the raw frame is still better than swallowing it.
    return data
  }
  if (typeof parsed !== 'object' || parsed === null) return data
  const { code, error, detail } = parsed as Record<string, unknown>
  const message = [error, detail].find((v) => typeof v === 'string' && v) as string | undefined
  if (typeof code === 'string' && code) {
    return message ? `${code} (${message})` : code
  }
  return message ?? data
}

/** The `code` of an `event: error` frame, for a caller that acts on it. */
function errorFrameCode(data: string): string | null {
  try {
    const code = (JSON.parse(data) as { code?: unknown } | null)?.code
    return typeof code === 'string' ? code : null
  } catch {
    return null
  }
}

/** An `event: error` frame; `code` is the stable half of it (cdb_tqs#898). */
export class StreamEventError extends Error {
  constructor(message: string, readonly code: string | null) {
    super(message)
    this.name = 'StreamEventError'
  }
}

/**
 * Consume an SSE body until its terminal event and resolve with the result.
 *
 * Both slow `/agent/*` endpoints here answer the same way: `: keepalive`
 * comments while the LLM call runs, then one `result` (or `error`) event. The
 * comments are the point — they keep the gateway from cutting a response that
 * has not started yet — so they are read and discarded rather than ignored by
 * accident.
 *
 * Shared so the two callers cannot drift on the parsing. `label` only appears
 * in error messages.
 */
async function readResultEvent<T>(
  stream: ReadableStream<Uint8Array>,
  label: string,
  onProgress?: (progress: RefineProgress) => void
): Promise<T> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        let eventName = "message";
        let data = "";
        for (const line of rawEvent.split("\n")) {
          if (line.startsWith(":")) continue; // keepalive comment
          if (line.startsWith("event:")) eventName = line.slice(6).trim();
          else if (line.startsWith("data:")) data += line.slice(5).trim();
        }
        if (eventName === "result") {
          return JSON.parse(data) as T;
        }
        if (eventName === "error") {
          throw new StreamEventError(`${label} failed: ${describeErrorFrame(data)}`, errorFrameCode(data));
        }
        if (eventName === "progress" && onProgress) {
          // A malformed progress frame must not fail a refinement that is
          // otherwise fine — it is a display detail, not the answer.
          try {
            onProgress(JSON.parse(data) as RefineProgress);
          } catch {
            // ignored on purpose
          }
        }
      }
      if (done) break;
    }
  } finally {
    reader.cancel().catch(() => {});
  }

  throw new Error(`${label} stream ended without a result`);
}

/** An HTTP error before the stream started, with the backend's `code` (cdb_tqs#899) when it sent one. */
export class StreamRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    label: string,
  ) {
    super(`${label} failed: HTTP ${status}${code ? ` (${code})` : ""}`);
  }
}

/**
 * POST a JSON body to a keepalive SSE endpoint and resolve with its `result`.
 *
 * Shared by the quality check and the tasks and summary of a recording
 * (cdb_tqs#2330, #2331), which answer the same way.
 */
export async function postForResultEvent<T>(
  url: string,
  payload: unknown,
  label: string,
): Promise<T> {
  const body = payload === undefined ? undefined : JSON.stringify(payload);
  const doFetch = (headers: Record<string, string>) =>
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      credentials: "include",
      body,
    });

  let response = await doFetch(await getAuthHeaders());
  // Retry once with a refreshed token, mirroring fetchWithLogging.
  if (response.status === 401) {
    const { authService } = await import("@/services/auth-service");
    const newToken = await authService.refreshAccessToken();
    if (newToken) {
      response = await doFetch({ Authorization: `Bearer ${newToken}` });
    }
  }

  if (!response.ok || !response.body) {
    let code: string | null = null;
    try {
      const detail = (await response.json())?.detail;
      code = typeof detail?.code === "string" ? detail.code : null;
    } catch {
      // no JSON body: the status says enough
    }
    throw new StreamRequestError(response.status, code, label);
  }

  return readResultEvent<T>(response.body, label);
}
